import { useCallback } from 'react';
import axios from 'axios';
import clientService from '../clientService';
import { FileUploadRequest, FileFinalizeRequest, FileAdminDetailWithUser } from '../api/Api';

interface UseFileUploadResult {
  uploadFile: (file: File, deploymentId: string, onProgress: (progress: number) => void) => Promise<FileAdminDetailWithUser | null>;
}

const useFileUpload = (): UseFileUploadResult => {
  const uploadFile = useCallback(async (file: File, deploymentId: string, onProgress: (progress: number) => void): Promise<FileAdminDetailWithUser | null> => {
    try {
      // 1. Get signed URL
      const uploadRequest: FileUploadRequest = {
        deployment_id: deploymentId,
        file_name: file.name,
        content_type: file.type || 'application/octet-stream',
      };
      const generateUrlResponse = await clientService.api.generateUploadUrlApiV1AdminFilesGenerateUploadUrlPost(uploadRequest);
      const { upload_url, gcs_path } = generateUrlResponse.data;

      // 2. Upload file to signed URL
      await axios.put(upload_url, file, {
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        },
      });

      // 3. Finalize upload
      const finalizeRequest: FileFinalizeRequest = {
        deployment_id: deploymentId,
        gcs_path: gcs_path,
        file_name: file.name,
      };
      const finalizeResponse = await clientService.api.finalizeUploadApiV1AdminFilesFinalizeUploadPost(finalizeRequest);

      return finalizeResponse.data; // Assuming this returns FileAdminDetailWithUser
    } catch (err) {
      console.error('File upload failed:', err);
      return null;
    }
  }, []);

  return { uploadFile };
};

export default useFileUpload;
